<template>
  <div style="height: 100%">
    <perfect-scrollbar class="scrollBody">
      <div class="wrap-edit-button">
        <Pack
          v-for="(item, index) in currentPresetOptions"
          :key="index"
          :ref="`button-pack-${index}`"
          :item="item"
          :index-item="index"
          :selected-pack="selectedPack"
          :hide-item-row="false"
          :is-ready-to-save="isReadyToSave"
          :invalid-pack="invalidPack"
          :type-pack="'button'"
          @validateItemName="validateItemName"
          @onChangePack="onChangePack"
          @onDelete="onDeleteButton"
          @onDuplicate="onDuplicateButton">
          <template #imageSlot>
            <ButtonItem
              :item="item"
              :item-style="getPreviewButtonCss(item)"
              :index-item="index"
              @onChangePack="onChangePack" />
          </template>
          <template #settingSlot>
            <EditButtonPanel
              :index-item="index"
              :is-ready-to-save="isReadyToSave"
              :button-item="item"
              :position-top-preview-button="positionTopPreviewButton"
              @destroy="commitButtonPanel"
              @onChangePack="onChangePack" />
          </template>
        </Pack>
        <div v-if="currentPresetOptions.length < BUTTON_MAX_LENGTH" class="wrap-btn-footer-action">
          <a class="btn btn-add-new" @click="onAddNewStyle">
            <i class="icon icon-add" />
            Add New Button Style
          </a>
        </div>
      </div>

      <GuideCustomizeStyle
        v-if="!obGlobalStyle.tooltipButtonStyle"
        title="Setup Button Style"
        guide="tooltipButtonStyle"
        link="https://help.gempages.net/articles/customize-global-style#table-content-5"
        content="These pre-defined styles are mapped to the corresponding button elements." />
    </perfect-scrollbar>

    <div id="preview-button" teleport></div>
    <div id="text-advance-setting" teleport></div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Pack from './/Pack.vue';
import EditButtonPanel from './/ButtonPanel.vue';
import ButtonMixin from '../../utils/mixins/Button';
import ButtonItem from './/ButtonItem.vue';
import { cloneDeep } from '@/utils/common';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { mutations } from '@modules/editor-sidebar';
import { presetOptionTypes } from '../../constants/styleConstant';
import GuideCustomizeStyle from '../guide/GuideCustomizeStyle.vue';
import HELPER from '@/app/utils/HELPER';

const BUTTON_MAX_LENGTH = 10;

export default defineComponent({
  name: 'Button',
  components: {
    Pack,
    ButtonItem,
    EditButtonPanel,
    GuideCustomizeStyle,
  },
  mixins: [ButtonMixin],
  data: () => ({
    selectedPack: -1,
    BUTTON_MAX_LENGTH,
    isReadyToSave: true,
    positionTopPreviewButton: 0,
    invalidPack: -1,
  }),
  computed: {
    ...mapGetters('sidebar', {
      currentPresetOptions: 'getCurrentPresetOptions',
    }),
    ...mapState('app', {
      shopMeta: 'shopMeta',
    }),
    ...mapState('sidebar', {
      styleIdTargetGlobalStyle: 'styleIdTargetGlobalStyle',
    }),
    obGlobalStyle() {
      return this.shopMeta.onboardingGlobalStyle;
    },
  },
  watch: {
    styleIdTargetGlobalStyle(val) {
      if (val) {
        const indexItem = this.currentPresetOptions.findIndex((item: any) => item.id == val);
        this.selectedPack = indexItem;
      }
    },
    selectedPack(val) {
      if (val != -1) {
        this.$nextTick(() => {
          const top = this.$refs[`button-pack-${val}`][0].$el.getBoundingClientRect().top - 100;
          this.positionTopPreviewButton = top;
        });
      } else {
        this.positionTopPreviewButton = 0;
      }
    },
    isReadyToSave(newVal) {
      this.$emit('isReadyToSave', newVal);
    },
  },
  created() {
    this.makeCurrentPresetOptions();
    this.setPresetTypeId(presetOptionTypes.button);
  },
  mounted() {
    this.setStyleIdTargetGS(parseInt(this.styleIdTargetGlobalStyle));
  },
  methods: {
    ...mapMutations('sidebar', {
      setPresetTypeId: mutations.SET_PRESET_TYPE_ID,
      setCurrentPresetOptions: mutations.SET_CURRENT_PRESET_OPTIONS,
      setStyleIdTargetGS: mutations.SET_STYLE_ID_TARGET_GLOBAL_STYLE,
    }),
    ...mapActions('app', {
      updateShopMeta: 'updateShopMeta',
    }),
    makeCurrentPresetOptions(): void {
      this.setCurrentPresetOptions(cloneDeep(this.buttons));
    },
    validateItemName(data: any): void {
      this.isReadyToSave = data.isAvailable;
      this.invalidPack = data.invalidPack;
    },
    onAddNewStyle() {
      const elementNames = this.currentPresetOptions.map((element: { name: any }) => element.name);
      let i = 1;
      while (elementNames.includes(this.generateCreateNewName(i))) i++;

      const copyButtonFirst = cloneDeep(this.currentPresetOptions[0]);
      // const countButton = this.currentPresetOptions.length;
      copyButtonFirst.id = `gpid_${HELPER.MakeId()}`;
      copyButtonFirst.name = this.generateCreateNewName(i);
      copyButtonFirst.key = '';
      copyButtonFirst.isDefault = false;
      this.currentPresetOptions.push(copyButtonFirst);
    },
    generateCreateNewName(index: number): string {
      return `Button ${index > 0 ? index : ''}`.trim();
    },
    onDuplicateButton(indexItem: number) {
      if (!this.isReadyToSave) return;
      if (this.currentPresetOptions.length === BUTTON_MAX_LENGTH) {
        return;
      }
      const iDuplicate = this.currentPresetOptions[indexItem];

      const elementNames = this.currentPresetOptions.map((element: { name: any }) => element.name);
      let i = 1;
      while (elementNames.includes(this.generateDuplicateName(iDuplicate.name, i))) i++;

      if (indexItem != -1) {
        this.currentPresetOptions.push({
          ...cloneDeep(this.currentPresetOptions[indexItem]),
          key: '',
          id: `gpid_${HELPER.MakeId()}`,
          isDefault: false,
          name: this.generateDuplicateName(iDuplicate.name, i),
        });
      }
    },
    generateDuplicateName(name: string, index: number): string {
      return `${name} ${index > 0 ? index : ''}`.trim();
    },
    onDeleteButton(index: any) {
      if (!this.isReadyToSave) return;
      if (this.selectedPack == index) {
        this.selectedPack = -1;
      }
      this.$nextTick(() => {
        if (index != -1) {
          if (!this.currentPresetOptions[index].id) {
            this.currentPresetOptions.splice(index, 1);
          } else {
            this.currentPresetOptions[index].destroy = true;
          }
        }
      });
    },
    getPreviewButtonCss(button: any) {
      const obj = this.convertButtonOptionToCssObject(button, 24, 'preview');
      if (obj['button-small']) {
        obj.button = {
          ...obj['button'],
          ...obj['button-small'],
        };
      } else if (obj['button-medium']) {
        obj.button = {
          ...obj['button'],
          ...obj['button-medium'],
        };
      } else if (obj['button-large']) {
        obj.button = {
          ...obj['button'],
          ...obj['button-large'],
        };
      }
      return obj;
    },
    commitButtonPanel(data: any) {
      this.currentPresetOptions[data.index] = data.value;
      this.setCurrentPresetOptions(cloneDeep(this.currentPresetOptions));
      return false;
    },
    onChangePack($event: any) {
      this.selectedPack = $event;
    },
  },
});
</script>

<style scoped lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.scrollBody {
  height: calc(100% - 125px);
  display: flex;
  flex-direction: column;
}

.wrap-edit-button {
  flex: 1;
  padding: 16px 12px 12px;
  font-size: 12px;

  :deep(.gf_pack__wrap) {
    :deep(.gf_pack__wrap-row) {
      padding: 4px;
      min-height: 32px;
      cursor: pointer;
      border: 1px solid map-get($hybrid-shade-dark, '200');
      border-radius: $radius;
      padding: 8px 8px;
      margin-bottom: 10px;
      position: relative;
      height: 64px;

      .center-col {
        flex: 1;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding-left: 8px;

        .lbl-input {
          font-size: 12px;
          background-color: transparent;
          border: 0;
          color: $highlight2;
          border-radius: $radius;
          padding: 4px !important;
          border: 1px solid transparent;
          cursor: text;
          margin-left: -4px;

          &[data-input-focus='true'] {
            margin-left: 0 !important;
          }
        }
      }

      .right-col {
        .wrap-absolute-action {
          .item-action {
            cursor: pointer;
          }
        }
      }

      .left-col {
        flex-shrink: 0;
        flex-grow: 0;
        justify-content: flex-start;

        .wrap-thumb-preview {
          background: #f2e8f7;
          width: 96px;
          height: 48px;
          display: flex;
          padding: 12px 4px;
          align-items: center;
          justify-content: center;
          border-radius: $radius;
          overflow: hidden;
        }
      }
    }
    :deep(.opened) {
      height: 64px;
    }
  }
  :deep(.gf_pack__wrap--selected) {
    border: 1px solid map-get($hybrid-shade-light, '500');
    border-radius: $radius;

    .gf_pack__wrap-row {
      margin-bottom: 0;
    }
    .gf_pack__wrap-row.opened {
      .left-col {
        display: none;
      }

      .center-col {
        padding-left: 0;
        padding-right: 4px;
      }
    }

    .wrap-panel-settings {
      .wap-edit-panel {
        border: 0;
      }
    }
  }

  .wrap-btn-footer-action {
    .btn-add-new {
      background: unset;
      border: 1px dashed map-get($hybrid-shade-dark, '100');
      border-radius: $radius;
      -moz-border-radius: $radius;
      -webkit-border-radius: $radius;
      position: relative;
      padding-top: 8px;
      padding-bottom: 8px;
      font-size: 12px;
      line-height: 14px;

      .icon-add {
        height: 16px;
        margin-right: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }

  .btn-preview {
    white-space: nowrap;
  }
}
</style>
